import s from './Start.module.scss';
import { yupResolver } from '@hookform/resolvers/yup';
import { StartData } from '../../model/types/start';
import { Controller, useForm } from 'react-hook-form';
import { Button, ButtonMobile } from '@/shared/ui/Button';
import { Checkbox } from '@/shared/ui/Checkbox/Checkbox';
import { startSchema } from '../../lib/validationSchema/StartSchema';
import { useStartMutation, useResendCodeMutation } from '../../api/registrationApi';
import {
  setRegistrationData,
  setRegistrationToken,
  setMaxDeepLink,
  setResendCooldown,
  setRemainingAttempts,
  setSendChannel,
  setLockedUntil,
  resetRegistrationCooldowns,
  restoreRegistrationSession,
  saveRegistrationSession,
} from '@/features/RegistrationForm';
import { useAppDispatch, useAppSelector } from '@/shared/lib/hooks/redux';
import { useEffect, useRef, useState } from 'react';
import { InputTel } from '@/shared/ui/InputTel/InputTel';
import { useFormError } from '@/shared/lib/hooks/useFormError';
import { YandexCaptcha } from '@/shared/ui/YandexCaptcha/YandexCaptcha';
import { useScreenDetector } from '@/shared/lib/hooks/useScreenDetector';
import { phoneNumberFormat } from '@/shared/lib/utils/phoneNumberFormat';
import { normalizeApiError } from '@/shared/lib/utils/apiError';
import { extractOtpDetails } from '@/shared/lib/utils/extractOtpDetails';
import { linkifyEmail } from '@/shared/lib/utils/linkifyEmail';
import { useAlert } from '@/shared/lib/hooks/useAlert';
import { Alert } from '@/shared/ui/Alert/Alert';
import { OtpChannel } from '@/shared/lib/otp/cooldowns';
import { nextStep } from '@/widgets/RegistrationStepper';
import { Link } from 'react-router-dom';
import { PRIVACY_POLICY_PDF, getUserAgreementPdf } from '@/shared/lib/env';

const FALLBACK_COOLDOWN_SECONDS = 60;
const MAX_ATTEMPTS = 5;
const LOCKOUT_MESSAGE = 'Слишком много попыток. Попробуйте снова через 10 минут или измените номер телефона';

export const Start = () => {
  const dispatch = useAppDispatch();
  const onAlert = useAlert();
  const [triggerStart, { isLoading, isSuccess, error, data, originalArgs }] = useStartMutation();
  const [
    triggerResend,
    { isLoading: isResendLoading, isSuccess: isResendSuccess, data: resendData, error: resendError },
  ] = useResendCodeMutation();
  const { isMobile } = useScreenDetector();
  const {
    data: { phoneNumber },
    registrationToken,
    sendChannel,
    cooldowns,
    remainingAttempts,
    lockedUntil,
    sessions,
  } = useAppSelector((state) => state.registration);

  const [captcha, setCaptcha] = useState('');
  const [visible, setVisible] = useState(false);
  const [resetCaptcha, setResetCaptcha] = useState(0);
  const [isConsent, setIsConsent] = useState(() => !!phoneNumber);
  const [nowSec, setNowSec] = useState(() => Math.floor(Date.now() / 1000));
  const chosenChannel = useRef<OtpChannel | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitted, isValid },
    setError,
    watch,
  } = useForm<StartData>({
    mode: 'onTouched',
    resolver: yupResolver(startSchema),
    defaultValues: {
      phoneNumber: phoneNumber || '',
    },
  });

  const phoneNumberWatch = watch('phoneNumber');
  const normalizedEntered = phoneNumberFormat(phoneNumberWatch);
  const existingSession = normalizedEntered ? sessions[normalizedEntered] : undefined;
  const isDirtyAfterSend = phoneNumber !== phoneNumberWatch;
  const isReturnToCode = !!phoneNumber && phoneNumberFormat(phoneNumber) === normalizedEntered;
  const isExpiredLockedSession =
    !!existingSession && typeof existingSession.lockedUntil === 'number' && existingSession.lockedUntil <= nowSec;
  const hasSession = !!existingSession && !isExpiredLockedSession;
  const enteredLockedUntil = existingSession?.lockedUntil ?? (isReturnToCode ? lockedUntil : null);
  const isPhoneLocked = typeof enteredLockedUntil === 'number' && enteredLockedUntil > nowSec;

  const disableButtons =
    isPhoneLocked ||
    (hasSession ? isLoading || isResendLoading : !captcha || isLoading || isResendLoading || !isValid || !isConsent);

  const handleCaptchaReset = () => {
    setCaptcha('');
    setResetCaptcha((i) => i + 1);
  };

  const onSuccessCaptcha = (token: string) => {
    setCaptcha(token);
  };

  useEffect(() => {
    if (error) {
      handleCaptchaReset();
    }
  }, [error]);

  useEffect(() => {
    if (!isPhoneLocked) return;
    const id = setInterval(() => setNowSec(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, [isPhoneLocked]);

  useEffect(() => {
    if (isDirtyAfterSend && phoneNumber) {
      handleCaptchaReset();
    }
  }, [phoneNumberWatch]);

  const navigateToCode = (channel: OtpChannel, normalizedPhone: string) => {
    dispatch(setRegistrationData({ phoneNumber: watch('phoneNumber') }));
    dispatch(restoreRegistrationSession(normalizedPhone));
    dispatch(setSendChannel(channel));
    dispatch(saveRegistrationSession(normalizedPhone));
    dispatch(nextStep());
  };

  const resolveSession = () => {
    if (existingSession && !isExpiredLockedSession) return existingSession;
    if (isReturnToCode && registrationToken && !(typeof lockedUntil === 'number' && lockedUntil <= nowSec)) {
      return { registrationToken, sendChannel, cooldowns, remainingAttempts, lockedUntil };
    }
    return null;
  };

  const submitChannel = (data: StartData, chosen: OtpChannel) => {
    const normalizedPhone = phoneNumberFormat(data.phoneNumber);
    if (isPhoneLocked || (normalizedPhone && normalizedPhone !== normalizedEntered)) return;
    chosenChannel.current = chosen;
    const session = resolveSession();
    if (session && session.registrationToken) {
      if (typeof session.lockedUntil === 'number' && session.lockedUntil > nowSec) {
        return;
      }
      if (session.sendChannel === chosen || session.cooldowns?.[chosen]?.timestamp) {
        navigateToCode(chosen, normalizedPhone);
        return;
      }
      triggerResend({ registrationToken: session.registrationToken, channel: chosen });
      return;
    }
    if (!captcha) return;
    triggerStart({ phoneNumber: normalizedPhone, captcha, channel: chosen });
  };

  const onSubmit = (data: StartData) => submitChannel(data, 'max');
  const onSubmitSms = (data: StartData) => submitChannel(data, 'sms');

  useEffect(() => {
    if (isValid) {
      setVisible(true);
    }
  }, [isValid]);

  useEffect(() => {
    if (!isSuccess || !data) return;
    dispatch(setRegistrationData({ phoneNumber: watch('phoneNumber') }));
    const channel = chosenChannel.current ?? 'max';
    dispatch(setRegistrationToken(data.registrationToken));
    dispatch(setMaxDeepLink(data.maxDeepLink ?? null));
    dispatch(resetRegistrationCooldowns());
    dispatch(setSendChannel(channel));
    dispatch(setResendCooldown({ channel, timestamp: Date.now(), seconds: data.retryAfterSeconds ?? 0 }));
    dispatch(setRemainingAttempts(data.remainingAttempts ?? MAX_ATTEMPTS));
    dispatch(saveRegistrationSession(phoneNumberFormat(watch('phoneNumber'))));
    dispatch(nextStep());
  }, [isSuccess, data, dispatch, watch]);

  useEffect(() => {
    if (!isResendSuccess || !resendData) return;
    const channel = chosenChannel.current ?? 'max';
    const normalizedPhone = phoneNumberFormat(watch('phoneNumber'));
    dispatch(restoreRegistrationSession(normalizedPhone));
    dispatch(setSendChannel(channel));
    dispatch(
      setResendCooldown({
        channel,
        timestamp: Date.now(),
        seconds: resendData.retryAfterSeconds ?? FALLBACK_COOLDOWN_SECONDS,
      }),
    );
    dispatch(setRemainingAttempts(resendData.remainingAttempts ?? MAX_ATTEMPTS));
    dispatch(setRegistrationData({ phoneNumber: watch('phoneNumber') }));
    dispatch(saveRegistrationSession(normalizedPhone));
    dispatch(nextStep());
  }, [isResendSuccess, resendData, dispatch, watch]);

  useEffect(() => {
    if (!resendError) return;
    const normalized = normalizeApiError(resendError);
    const channel = chosenChannel.current ?? 'max';
    const normalizedPhone = phoneNumberFormat(watch('phoneNumber'));
    const { retryAfterSeconds, lockedUntil, remainingAttempts } = extractOtpDetails(normalized);
    if (typeof retryAfterSeconds === 'number') {
      dispatch(setResendCooldown({ channel, timestamp: Date.now(), seconds: retryAfterSeconds }));
    }
    if (typeof lockedUntil === 'number') {
      dispatch(setLockedUntil(lockedUntil));
    }
    if (typeof remainingAttempts === 'number') {
      dispatch(setRemainingAttempts(remainingAttempts));
    }
    if (typeof lockedUntil === 'number' || remainingAttempts === 0) {
      dispatch(saveRegistrationSession(normalizedPhone));
      return;
    }
    onAlert(linkifyEmail(normalized?.message || 'Не удалось отправить код. Попробуйте позже.'), true);
  }, [resendError, dispatch, onAlert]);

  useEffect(() => {
    if (!error) return;
    const normalized = normalizeApiError(error);
    if (!normalized) return;
    const { retryAfterSeconds, registrationToken, remainingAttempts, lockedUntil } = extractOtpDetails(normalized);
    if (normalized.code === 'OTP_RESEND_COOLDOWN' && registrationToken) {
      const channel = chosenChannel.current ?? 'max';
      dispatch(setRegistrationData({ phoneNumber: watch('phoneNumber') }));
      dispatch(setRegistrationToken(registrationToken));
      dispatch(setSendChannel(channel));
      if (typeof retryAfterSeconds === 'number') {
        dispatch(setResendCooldown({ channel, timestamp: Date.now(), seconds: retryAfterSeconds }));
      }
      if (typeof remainingAttempts === 'number') {
        dispatch(setRemainingAttempts(remainingAttempts));
      }
      if (typeof lockedUntil === 'number') {
        dispatch(setLockedUntil(lockedUntil));
      }
      dispatch(saveRegistrationSession(phoneNumberFormat(watch('phoneNumber'))));
      if (typeof lockedUntil === 'number' && lockedUntil > Date.now() / 1000) return;
      dispatch(nextStep());
    }
  }, [error, dispatch, watch]);

  const normalizedError = normalizeApiError(error);
  const isRegisteredPhoneError = normalizedError?.code === 'USER_ALREADY_EXIST';
  const registeredPhoneMessage =
    isRegisteredPhoneError && originalArgs?.phoneNumber === normalizedEntered ? normalizedError.message : null;
  const isCooldownError =
    normalizedError?.code === 'OTP_RESEND_COOLDOWN' && !!extractOtpDetails(normalizedError).registrationToken;
  useFormError<{ phoneNumber: string }>(
    isCooldownError || isRegisteredPhoneError ? null : error,
    setError,
    handleCaptchaReset,
  );

  return (
    <div className={s.wrapper}>
      <form className={s.inputs}>
        {isPhoneLocked && <Alert className={s.alert}>{LOCKOUT_MESSAGE}</Alert>}
        {registeredPhoneMessage && <Alert className={s.alert}>{registeredPhoneMessage}</Alert>}

        <Controller
          name="phoneNumber"
          control={control}
          render={({ field }) => (
            <InputTel
              {...field}
              label={'Номер телефона'}
              placeholder={'+7 900 000-00-00'}
              error={errors.phoneNumber?.message}
              isSubmitted={isSubmitted}
              baseAddon={false}
            />
          )}
        />

        {visible && !hasSession && <YandexCaptcha onSuccess={onSuccessCaptcha} resetCaptcha={resetCaptcha} />}

        <div className={s.consent}>
          <Checkbox
            className={s.checkbox}
            checked={isConsent}
            onChange={() => setIsConsent((v) => !v)}
            color={isMobile ? 'green' : 'default'}
            size={'s'}
          />
          <p className={s.consentText}>
            Я даю{' '}
            <Link target="_blank" to={getUserAgreementPdf()}>
              согласие
            </Link>{' '}
            на обработку моих персональных данных, с{' '}
            <Link target="_blank" to={PRIVACY_POLICY_PDF}>
              политикой конфиденциальности
            </Link>{' '}
            ознакомлен
          </p>
        </div>

        <div className={s.actions}>
          {isMobile ? (
            <>
              <ButtonMobile type={'submit'} disabled={disableButtons} onClick={handleSubmit(onSubmit)}>
                Получить код в Макс
              </ButtonMobile>

              <ButtonMobile
                variant={'ghost'}
                color={'green'}
                disabled={disableButtons}
                onClick={handleSubmit(onSubmitSms)}
              >
                Отправить код по СМС
              </ButtonMobile>
            </>
          ) : (
            <>
              <Button
                type={'submit'}
                textSize={'b2'}
                disabled={disableButtons}
                size={'m'}
                onClick={handleSubmit(onSubmit)}
              >
                Получить код в Макс
              </Button>

              <Button
                textSize={'b2'}
                variant={'ghost'}
                disabled={disableButtons}
                size={'m'}
                height={'48px'}
                onClick={handleSubmit(onSubmitSms)}
              >
                Отправить код по СМС
              </Button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};
